import type {
  BoatData,
  BoatDataPoint,
  BoatTab,
  RangeEstimatorDataset,
  RangeEstimatorDatasetSummary,
} from "./types";

const API_BASE =
  process.env.NEXT_PUBLIC_API_URL ||
  process.env.API_URL ||
  "https://internal.electrifiedmarina.com";

type JsonRecord = Record<string, unknown>;
type NextFetchRequestInit = RequestInit & {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

function rangeFetchOptions(): NextFetchRequestInit {
  if (typeof window !== "undefined") {
    return { cache: "no-store" };
  }

  return {
    next: { revalidate: 300, tags: ["range-estimator"] },
  };
}

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function get(record: JsonRecord, keys: string[]): unknown {
  for (const key of keys) {
    if (key in record) return record[key];
  }
  return undefined;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function collectionFromPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  const data = payload.data;
  if (Array.isArray(data)) return data;

  const datasets = payload.datasets;
  if (Array.isArray(datasets)) return datasets;

  const items = payload.items;
  if (Array.isArray(items)) return items;

  return [];
}

function datasetFromPayload(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;

  if (isRecord(payload.data)) return payload.data;
  if (isRecord(payload.dataset)) return payload.dataset;

  return payload;
}

function isExplicitFalse(value: unknown): boolean {
  return value === false || value === 0 || value === "0" || value === "false";
}

function isUnavailable(record: JsonRecord): boolean {
  if (isExplicitFalse(get(record, ["is_public", "public"]))) return true;
  if (isExplicitFalse(get(record, ["is_complete", "complete", "is_valid", "valid"]))) return true;

  const status = asString(get(record, ["validation_status", "status"]));
  return Boolean(status && ["draft", "private", "incomplete", "invalid"].includes(status.toLowerCase()));
}

function nameFromRecord(record: JsonRecord): string | null {
  const publicLabel = asString(get(record, ["label"]));
  if (publicLabel) return publicLabel;

  const item = get(record, ["item"]);
  if (isRecord(item)) {
    const itemDirect = asString(get(item, ["label", "name", "title", "item_name", "itemName", "boat_name", "boatName"]));
    if (itemDirect) return itemDirect;
  }

  const source = isRecord(item) ? item : record;
  const boatName =
    asString(get(record, ["item_name", "itemName", "boat_name", "boatName"])) ??
    asString(get(source, ["item_name", "itemName", "boat_name", "boatName"]));
  if (boatName) return boatName;

  const year = asNumber(get(source, ["year"]));
  const make = asString(get(source, ["make"]));
  const model = asString(get(source, ["model"]));
  const parts = [year ? String(Math.round(year)) : null, make, model].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");

  return asString(get(record, ["name", "title"]));
}

function normalizeSummary(raw: unknown, fallbackId?: number): RangeEstimatorDatasetSummary | null {
  if (!isRecord(raw) || isUnavailable(raw)) return null;

  const item = get(raw, ["item"]);
  const itemRecord = isRecord(item) ? item : null;
  const id = asNumber(get(raw, ["id", "dataset_id", "datasetId", "sheetId"])) ?? fallbackId ?? null;
  const name = nameFromRecord(raw);
  if (!id || !name) return null;

  const itemId =
    asNumber(get(raw, ["item_id", "itemId"])) ??
    (itemRecord ? asNumber(get(itemRecord, ["id"])) : null);
  const make = asString(get(raw, ["make"])) ?? (itemRecord ? asString(get(itemRecord, ["make"])) : null);
  const model = asString(get(raw, ["model"])) ?? (itemRecord ? asString(get(itemRecord, ["model"])) : null);
  const year = asNumber(get(raw, ["year"])) ?? (itemRecord ? asNumber(get(itemRecord, ["year"])) : null);

  return {
    id,
    itemId,
    name,
    make,
    model,
    year: year ? Math.round(year) : null,
  };
}

function normalizeBatteryCapacity(raw: unknown): number | null {
  const direct = asNumber(raw);
  if (direct && direct > 0) return direct;

  if (!isRecord(raw)) return null;

  const wh = asNumber(
    get(raw, [
      "capacity_wh",
      "capacityWh",
      "watt_hours",
      "wattHours",
      "wh",
      "value",
      "capacity",
    ])
  );
  if (wh && wh > 0) return wh;

  const kwh = asNumber(get(raw, ["capacity_kwh", "capacityKwh", "kwh"]));
  return kwh && kwh > 0 ? kwh * 1000 : null;
}

function normalizeBatteries(record: JsonRecord): number[] {
  const source = get(record, ["batteries", "battery_options", "batteryOptions", "battery_capacities", "batteryCapacities"]);
  const rawValues = Array.isArray(source)
    ? source
    : typeof source === "string"
      ? source.split(",")
      : [];

  return Array.from(
    new Set(
      rawValues
        .map(normalizeBatteryCapacity)
        .filter((capacity): capacity is number => Boolean(capacity && capacity > 0))
    )
  ).sort((a, b) => a - b);
}

function normalizeDataPoint(raw: unknown): BoatDataPoint | null {
  if (!isRecord(raw)) return null;

  const throttle = asNumber(get(raw, ["throttle", "throttle_percent", "throttlePercent"]));
  const rpm = asNumber(get(raw, ["rpm"]));
  const wattage = asNumber(get(raw, ["wattage", "watts", "power_draw_watts", "powerDrawWatts", "power_watts", "powerWatts"]));
  const speed = asNumber(get(raw, ["speed", "speed_knots", "speedKnots", "speed_kts", "speedKts"]));

  if (
    throttle === null ||
    rpm === null ||
    wattage === null ||
    speed === null ||
    throttle < 0 ||
    throttle > 100 ||
    rpm < 0 ||
    wattage <= 0 ||
    speed <= 0
  ) {
    return null;
  }

  return { throttle, rpm, wattage, speed };
}

function normalizeDataPoints(record: JsonRecord): BoatDataPoint[] | null {
  const source = get(record, [
    "dataPoints",
    "data_points",
    "performance_rows",
    "performanceRows",
    "measured_rows",
    "measuredRows",
    "rows",
  ]);
  if (!Array.isArray(source)) return null;

  const seen = new Set<number>();
  const rows: BoatDataPoint[] = [];

  for (const rawRow of source) {
    const row = normalizeDataPoint(rawRow);
    if (!row) return null;

    const throttleKey = Math.round(row.throttle * 1000) / 1000;
    if (seen.has(throttleKey)) return null;

    seen.add(throttleKey);
    rows.push(row);
  }

  rows.sort((a, b) => a.throttle - b.throttle);
  return rows.length >= 3 ? rows : null;
}

function normalizeDataset(raw: unknown, fallbackId?: number): RangeEstimatorDataset | null {
  const payload = datasetFromPayload(raw);
  if (!isRecord(payload)) return null;

  const summary = normalizeSummary(payload, fallbackId);
  if (!summary) return null;

  const batteries = normalizeBatteries(payload);
  const dataPoints = normalizeDataPoints(payload);
  if (batteries.length === 0 || !dataPoints) return null;

  return {
    ...summary,
    sheetId: summary.id,
    batteries,
    dataPoints,
  };
}

function normalizeDatasetIds(datasetIds?: number[]): number[] | undefined {
  if (datasetIds === undefined) return undefined;

  return Array.from(
    new Set(
      datasetIds
        .map((id) => Math.trunc(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    )
  );
}

function sortAndFilterByIds<T extends { id: number }>(items: T[], datasetIds?: number[]): T[] {
  const ids = normalizeDatasetIds(datasetIds);
  if (!ids) return items;
  if (ids.length === 0) return [];

  const allowed = new Set(ids);
  const order = new Map(ids.map((id, index) => [id, index]));

  return items
    .filter((item) => allowed.has(item.id))
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

export async function fetchRangeDatasetSummaries(
  datasetIds?: number[]
): Promise<RangeEstimatorDatasetSummary[]> {
  const ids = normalizeDatasetIds(datasetIds);
  if (ids && ids.length === 0) return [];

  const url = new URL(`${API_BASE}/api/public/range-estimator/datasets`);
  if (ids && ids.length > 0) url.searchParams.set("ids", ids.join(","));

  try {
    const res = await fetch(url.toString(), rangeFetchOptions());
    if (!res.ok) return [];

    const payload = await res.json();
    const summaries = collectionFromPayload(payload)
      .map((raw) => normalizeSummary(raw))
      .filter((summary): summary is RangeEstimatorDatasetSummary => Boolean(summary));

    return sortAndFilterByIds(summaries, ids);
  } catch (error) {
    console.error("Range estimator dataset list failed", error);
    return [];
  }
}

export async function fetchRangeDataset(id: number): Promise<RangeEstimatorDataset | null> {
  const datasetId = Math.trunc(id);
  if (!Number.isFinite(datasetId) || datasetId <= 0) return null;

  try {
    const res = await fetch(
      `${API_BASE}/api/public/range-estimator/datasets/${datasetId}`,
      rangeFetchOptions()
    );
    if (!res.ok) return null;

    return normalizeDataset(await res.json(), datasetId);
  } catch (error) {
    console.error(`Range estimator dataset ${datasetId} failed`, error);
    return null;
  }
}

function boatDataFromDataset(dataset: RangeEstimatorDataset): BoatData {
  return {
    id: dataset.id,
    itemId: dataset.itemId,
    name: dataset.name,
    sheetId: dataset.id,
    batteries: dataset.batteries,
    dataPoints: dataset.dataPoints,
  };
}

export async function fetchBoatDatasets(datasetIds?: number[]): Promise<BoatData[]> {
  const summaries = await fetchRangeDatasetSummaries(datasetIds);
  if (summaries.length === 0) return [];

  const datasets = await Promise.all(
    summaries.map((summary) => fetchRangeDataset(summary.id))
  );

  return datasets
    .filter((dataset): dataset is RangeEstimatorDataset => Boolean(dataset))
    .map(boatDataFromDataset);
}

export async function fetchBoatTabs(datasetIds?: number[]): Promise<BoatTab[]> {
  const summaries = await fetchRangeDatasetSummaries(datasetIds);
  return summaries.map((summary) => ({
    id: summary.id,
    name: summary.name,
    sheetId: summary.id,
    itemId: summary.itemId,
  }));
}

export async function fetchBoatData(tab: BoatTab): Promise<BoatData | null> {
  const datasetId = tab.id ?? tab.sheetId;
  const dataset = await fetchRangeDataset(datasetId);
  if (!dataset) return null;

  return boatDataFromDataset(dataset);
}
